import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useState } from "react";
import { Syndicat } from "../../lib/interfaces/syndicat.interface";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { toast } from "sonner";
import { AtSign, Building2, MapPin, Phone, Search } from "lucide-react";
import { useFindSyndicatsAffilies } from "../../lib/hooks/useReferentiels";

const TrouverSyndicatButton = () => {
  const [parent] = useAutoAnimate();
  const [departement, setDepartement] = useState("");
  const [syndicats, setSyndicats] = useState<Syndicat[] | null>(null);
  const { isPending, mutate: rechercher } = useFindSyndicatsAffilies();
  const rechercherSyndicats = (departement: string) => rechercher(departement, {
    onSuccess: (data) => {
      setSyndicats(data);
    },
    onError: () => {
      toast.error("Une erreur s'est produite.");
    },
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          className="w-full bg-[#efcc5c] text-[#21352e] hover:bg-[#f5d875] focus-visible:ring-[#efcc5c]"
          icon={<Search className="h-4 w-4" />}
        >
          Trouver un syndicat
        </Button>
      </DialogTrigger>

      <DialogContent className="gap-0 overflow-hidden border-slate-200 p-0 sm:max-w-[560px] sm:rounded-2xl">
        <DialogHeader className="border-b border-slate-100 bg-[#fbfaf6] px-6 py-5 pr-12">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-xl text-slate-950">Trouver un syndicat</DialogTitle>
              <DialogDescription className="mt-1.5 leading-6">
                Indiquez votre département pour afficher les syndicats affiliés au SNA.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              rechercherSyndicats(departement);
            }}
          >
            <Label htmlFor="dpt" className="font-semibold text-slate-800">
              Numéro de département
            </Label>
            <div className="mt-2 flex flex-col gap-3 sm:flex-row">
              <Input
                className="h-11 flex-1"
                required
                id="dpt"
                inputMode="numeric"
                placeholder="Ex. 29"
                value={departement}
                onChange={(e) => setDepartement(e.currentTarget.value)}
              />
              <Button className="h-11 bg-[#173f35] hover:bg-[#23594a]" loading={isPending} type="submit">
                Rechercher
              </Button>
            </div>
          </form>

          {/* Aucun résultat */}
          {!!syndicats && syndicats.length === 0 && (
            <div className="mt-6 rounded-xl border border-[#e4d8aa] bg-[#fff9e8] px-4 py-3 text-sm text-[#705616]">
              Aucun syndicat affilié trouvé pour ce département.
            </div>
          )}

          {/* Liste des résultats */}
          <div className="mt-6 flex flex-col gap-3" ref={parent}>
            {!!syndicats && syndicats.length > 0 && (
              <>
                <div className="flex items-center justify-between gap-4">
                  <h3 className="font-bold text-slate-950">Syndicats trouvés</h3>
                  <span className="text-sm text-slate-500">Département {departement}</span>
                </div>
                {syndicats.map((syndicat) => (
                  <article className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" key={syndicat.id}>
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#deece5] text-[#255b4b]">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-slate-900">{syndicat.nom}</h4>
                        <div className="mt-2 space-y-1.5 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                            <span>{syndicat.telephone || "Non renseigné"}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AtSign className="h-4 w-4 shrink-0 text-slate-400" />
                            <span className="break-all">{syndicat.email || "Non renseigné"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrouverSyndicatButton;
